"use client";

import React from "react";
import { motion } from "framer-motion";
import { ThermometerSnowflake, ThermometerSun, Wrench, CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle, CalendarClock, ShieldCheck, Banknote, Clock } from 'lucide-react';
import { Separator } from "@/components/ui/separator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
const services = [{
  title: "Cooling (AC & Heat Pumps)",
  icon: ThermometerSnowflake,
  items: ["Diagnostic testing and system inspection", "Refrigerant recharging", "Compressor repair or replacement", "Condenser coil cleaning", "Capacitor and contactor replacement", "Thermostat installation & programming"]
}, {
  title: "Heating (Furnaces & Heat Pumps)",
  icon: ThermometerSun,
  items: ["Annual furnace tune-up and inspection", "Blower motor repair", "Heat exchanger inspection", "Ignition and pilot assembly service", "Ductwork sealing and efficiency upgrades", "Heating restoration & inspection"]
}, {
  title: "System Installation & Replacement",
  icon: Wrench,
  items: ["High-efficiency cooling system installation", "Heat pump installation (heating + cooling)", "Complete ductwork design & installation", "Smart thermostat integration", "System commissioning & testing", "Manufacturer warranty on equipment"]
}];
const pricing = [{
  service: "Seasonal Tune-Up",
  price: "Quote from your pro"
}, {
  service: "System Diagnostic",
  price: "Quote from your pro"
}, {
  service: "Urgent AC Service",
  price: "Quote from your pro"
}, {
  service: "Refrigerant Recharge",
  price: "Quote from your pro"
}, {
  service: "Compressor Replacement",
  price: "Quote from your pro"
}, {
  service: "Full System Install",
  price: "Quote from your pro"
}];
const reasons = [{
  title: "Independent Local Pros",
  desc: "Local HVAC professionals connected through network onboarding"
}, {
  title: "Prompt Availability",
  desc: "Ask your pro about scheduling and arrival times for AC/heating needs"
}, {
  title: "Energy-Efficient Options",
  desc: "Pros install systems designed to optimize energy usage"
}, {
  title: "Warranty Coverage",
  desc: "Warranty terms are set by the pro and manufacturer - confirm before work begins"
}, {
  title: "Flexible Scheduling",
  desc: "Connect with available pros across the Atlanta area"
}, {
  title: "Upfront Quotes",
  desc: "Your pro provides a clear quote before any work starts"
}];
const problems = [{
  issue: "AC not cooling in summer?",
  usually: "Refrigerant leak, compressor failure, or thermostat malfunction.",
  time: "Varies by repair",
  cost: "Quote from your pro"
}, {
  issue: "Furnace not heating in winter?",
  usually: "Thermostat failure, ignition issue, or blower motor problem.",
  time: "Varies by repair",
  cost: "Quote from your pro"
}, {
  issue: "System running constantly but not reaching temperature?",
  usually: "Refrigerant leak, airflow restriction, or compressor wear.",
  time: "Varies by repair",
  cost: "Quote from your pro"
}, {
  issue: "High utility bills?",
  usually: "System running inefficiently due to dirty coils, low refrigerant, or aged system.",
  time: "Varies by repair",
  cost: "Quote from your pro"
}];
const brands = ["Trane", "Carrier", "Lennox", "Rheem", "York", "Goodman", "Bryant", "American Standard", "Daikin", "Fujitsu", "Mitsubishi", "LG", "Panasonic"];
const faqs = [{
  q: "How often should I maintain my HVAC system?",
  a: "Twice yearly is standard practice — spring AC check and fall furnace check. Filter replacement every 1–3 months is also recommended."
}, {
  q: "What's included in a tune-up?",
  a: "Tune-ups typically include filter replacement, coil inspection, refrigerant check, electrical inspection, and an overall efficiency evaluation."
}, {
  q: "How much does a new HVAC system cost?",
  a: "Pricing depends on the system size, efficiency rating, and home requirements. Your pro gives a quote before work starts."
}, {
  q: "Do pros offer urgent or after-hours service?",
  a: "Availability varies by contractor. Ask your pro about availability when submitting your request."
}, {
  q: "What's the warranty on new installations?",
  a: "Warranty terms are set by the pro and manufacturer - confirm coverage details before work begins."
}, {
  q: "How long does a system installation take?",
  a: "A system replacement typically takes 1–3 days depending on ductwork and installation complexity."
}, {
  q: "Who performs the HVAC work?",
  a: "Handy Heroes connects you with independent local HVAC pros who join through network onboarding."
}, {
  q: "Can pros install a smart thermostat?",
  a: "Yes. Many pros install and program Nest, Ecobee, and other smart thermostats."
}];
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export default function HvacGuideContent() {
  return <section id="hvacguidecontent" className="bg-background py-16 md:py-24">
      <div className="container mx-auto max-w-4xl px-4 md:px-6">
        
        {}
        <motion.article initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-100px"
      }} variants={fadeInUp} className="prose prose-slate dark:prose-invert max-w-none">
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl mb-6">
            Professional HVAC Service for Atlanta's Climate
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-4">
            Atlanta summers are hot and winters are cold — your HVAC system works hard year-round. 
            When your air conditioning fails during a 95-degree heat wave or your furnace quits in 
            freezing weather, you need immediate expert service, not promises.
          </p>
          <p className="text-lg text-muted-foreground leading-relaxed mb-6">
            Handy Heroes connects you with local independent HVAC pros across the Atlanta area. 
            Find pros for routine maintenance, diagnostics, and complete system replacement. 
            Your pro gives a quote before work starts.
          </p>
          <h3 className="font-default text-2xl font-bold text-foreground mb-6">
            Our HVAC Services Overview
          </h3>
        </motion.article>

        <Separator className="my-12" />

        {}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-100px"
      }} variants={staggerContainer}>
          <motion.h3 variants={fadeInUp} className="font-default text-2xl font-bold text-foreground mb-8">
            HVAC Services We Provide
          </motion.h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, i) => <motion.div key={i} data-index={i} variants={fadeInUp} className="flex flex-col">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <service.icon className="h-6 w-6" />
                </div>
                <h4 className="font-default text-xl font-semibold text-foreground mb-4">{service.title}</h4>
                <ul className="space-y-2 text-muted-foreground">
                  {service.items.map((item, j) => <li key={j} data-index={j} className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm">{item}</span>
                    </li>)}
                </ul>
              </motion.div>)}
          </div>
        </motion.div>

        <Separator className="my-12" />

        {}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-100px"
      }} variants={fadeInUp}>
          <h3 className="font-default text-2xl font-bold text-foreground mb-6">
            Transparent HVAC Pricing
          </h3>
          <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="px-6 py-4 font-medium">Service</th>
                  <th className="px-6 py-4 font-medium">Price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {pricing.map((item, i) => <tr key={i} data-index={i} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4 font-medium text-foreground">{item.service}</td>
                    <td className="px-6 py-4 text-muted-foreground">{item.price}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm font-medium text-foreground flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Your pro gives a quote before work starts.
          </p>
        </motion.div>

        <Separator className="my-12" />

        {}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-100px"
      }} variants={staggerContainer}>
          <motion.h3 variants={fadeInUp} className="font-default text-2xl font-bold text-foreground mb-8">
            Why Use Handy Heroes for HVAC?
          </motion.h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {reasons.map((reason, i) => <motion.div key={i} data-index={i} variants={fadeInUp} className="flex gap-4">
                <div className="mt-1 shrink-0">
                  <CheckCircle2 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-default font-semibold text-foreground">{reason.title}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{reason.desc}</p>
                </div>
              </motion.div>)}
          </div>
        </motion.div>

        <Separator className="my-12" />

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-100px"
        }} variants={fadeInUp}>
            <h3 className="font-default text-2xl font-bold text-foreground mb-4">
              HVAC Maintenance = Money Saved
            </h3>
            <p className="text-muted-foreground mb-4">A well-maintained HVAC system:</p>
            <ul className="space-y-3 mb-6">
              {["Runs more efficiently (lower energy bills)", "Helps extend equipment service life", "Helps prevent unexpected breakdowns", "Maintains system performance"].map((item, i) => <li key={i} data-index={i} className="flex items-center gap-3 text-muted-foreground">
                  <div className="h-1.5 w-1.5 rounded-lg bg-primary shrink-0" />
                  <span>{item}</span>
                </li>)}
            </ul>
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-4">
                <p className="text-sm text-foreground">
                  <strong className="font-semibold">Comprehensive tune-ups include:</strong> Filter replacement, coil check, refrigerant inspection, electrical connection review, and operational testing.
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-100px"
        }} variants={fadeInUp}>
            <h3 className="font-default text-2xl font-bold text-foreground mb-6">
              When to Call for HVAC Service
            </h3>
            
            <div className="mb-6">
              <h4 className="font-default font-semibold text-destructive flex items-center gap-2 mb-3">
                <AlertTriangle className="h-5 w-5" />
                Prompt Service Needed:
              </h4>
              <ul className="space-y-2 text-muted-foreground pl-7">
                <li className="list-disc">AC not cooling / Furnace not heating</li>
                <li className="list-disc">System making unusual noises</li>
                <li className="list-disc">Refrigerant leak suspected</li>
                <li className="list-disc">Thermostat malfunction</li>
              </ul>
            </div>

            <div>
              <h4 className="font-default font-semibold text-foreground flex items-center gap-2 mb-3">
                <CalendarClock className="h-5 w-5 text-primary" />
                Schedule Maintenance:
              </h4>
              <ul className="space-y-2 text-muted-foreground pl-7">
                <li className="list-disc">Annual spring AC check (before summer heat)</li>
                <li className="list-disc">Annual fall furnace check (before winter cold)</li>
                <li className="list-disc">Every 1–3 months: Filter replacement</li>
              </ul>
            </div>
          </motion.div>
        </div>

        <Separator className="my-12" />

        {}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-100px"
      }} variants={staggerContainer}>
          <motion.h3 variants={fadeInUp} className="font-default text-2xl font-bold text-foreground mb-8">
            Common HVAC Problems We Fix
          </motion.h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {problems.map((problem, i) => <motion.div key={i} data-index={i} variants={fadeInUp}>
                <Card className="h-full">
                  <CardContent className="p-6 flex flex-col h-full">
                    <h4 className="font-default font-semibold text-foreground mb-3 text-lg">
                      {problem.issue}
                    </h4>
                    <p className="text-sm text-muted-foreground mb-4 flex-grow">
                      <strong className="text-foreground font-medium">Usually:</strong> {problem.usually}
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground pt-4 border-t border-border">
                      <span className="flex items-center gap-1.5">
                        <Clock className="h-4 w-4 text-primary" />
                        {problem.time}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Banknote className="h-4 w-4 text-primary" />
                        {problem.cost}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </motion.div>

        <Separator className="my-12" />

        {}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-100px"
      }} variants={fadeInUp} className="text-center">
          <h3 className="font-default text-2xl font-bold text-foreground mb-6">
            HVAC Brands Local Pros Repair in Atlanta
          </h3>
          <div className="flex flex-wrap justify-center gap-3">
            {brands.map((brand, i) => <Badge key={i} data-index={i} variant="secondary" className="text-sm px-4 py-1.5">
                {brand}
              </Badge>)}
          </div>
        </motion.div>

        <Separator className="my-12" />

        {}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-100px"
      }} variants={fadeInUp}>
          <h3 className="font-default text-2xl font-bold text-foreground mb-8 text-center">
            FAQ — HVAC Services
          </h3>
          <Accordion type="single" collapsible className="w-full max-w-3xl mx-auto">
            {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left font-default text-base">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>

      </div>
    </section>;
}